/**
 * A small, safe Markdown reader for help articles. It returns a typed token tree, never an HTML
 * string: the UI renders each token as a React element, so article text can never become markup.
 *
 * Supported: headings (levels 1-3), paragraphs, bullet and numbered lists, fenced code blocks,
 * **bold**, *italic*, `code` and [links](href). Everything else (raw HTML, images, tables, `_` emphasis)
 * stays plain text. A link survives only with an `http:`, `https:` or `mailto:` address or an
 * app-relative `/path`; otherwise just its label is kept.
 */

export type Inline =
  | { type: "text"; text: string }
  | { type: "strong"; children: Inline[] }
  | { type: "em"; children: Inline[] }
  | { type: "code"; text: string }
  | { type: "link"; href: string; children: Inline[] };

export type Block =
  | { type: "heading"; level: 1 | 2 | 3; children: Inline[] }
  | { type: "paragraph"; children: Inline[] }
  | { type: "list"; ordered: boolean; items: Inline[][] }
  | { type: "code_block"; lang: string | null; text: string };

/** Emphasis inside emphasis inside a link stops being parsed past this depth (it stays text). */
const MAX_INLINE_DEPTH = 4;

/**
 * The href itself when it is safe to put in an anchor, else null. Whitespace and control characters
 * anywhere are refused (browsers ignore tabs and newlines inside a scheme: `java\tscript:`), as are
 * protocol-relative (`//host`) and backslash forms that browsers read as one.
 */
export function safeHref(raw: string): string | null {
  const href = raw.trim();
  // eslint-disable-next-line no-control-regex
  if (!href || href !== raw || /[\u0000- \u007f\\]/.test(href)) return null;
  if (href.startsWith("/")) return href.startsWith("//") ? null : href;
  if (/^mailto:[^@\s]+@\S+$/i.test(href)) return href;
  if (!/^https?:\/\//i.test(href)) return null;
  try {
    const url = new URL(href);
    return url.protocol === "http:" || url.protocol === "https:" ? href : null;
  } catch {
    return null;
  }
}

const isSpace = (c: string | undefined) => c === undefined || /\s/.test(c);

/** The `)` that closes a link target starting at `from`, allowing balanced pairs inside it; -1 if none. */
function closingParen(s: string, from: number): number {
  let depth = 0;
  for (let i = from; i < s.length; i++) {
    if (s[i] === "(") depth += 1;
    else if (s[i] === ")" && depth-- === 0) return i;
  }
  return -1;
}

/** Appends text, merging with a text token just before it. */
function pushText(out: Inline[], text: string): void {
  if (!text) return;
  const last = out[out.length - 1];
  if (last?.type === "text") last.text += text;
  else out.push({ type: "text", text });
}

function parseInline(s: string, depth = 0, allowLinks = true): Inline[] {
  const out: Inline[] = [];
  let i = 0;
  while (i < s.length) {
    const c = s[i]!;
    if (c === "\\" && i + 1 < s.length && /[!-/:-@[-`{-~]/.test(s[i + 1]!)) {
      pushText(out, s[i + 1]!);
      i += 2;
    } else if (c === "`") {
      const end = s.indexOf("`", i + 1);
      if (end > i + 1) {
        out.push({ type: "code", text: s.slice(i + 1, end) });
        i = end + 1;
      } else {
        pushText(out, c);
        i += 1;
      }
    } else if (c === "*" && s[i + 1] === "*" && depth < MAX_INLINE_DEPTH) {
      let end = s.indexOf("**", i + 2);
      // in a run of stars the last two close the bold: **a *b***
      while (end >= 0 && s[end + 2] === "*") end += 1;
      if (end > i + 2 && !isSpace(s[i + 2]) && !isSpace(s[end - 1])) {
        out.push({ type: "strong", children: parseInline(s.slice(i + 2, end), depth + 1, allowLinks) });
        i = end + 2;
      } else {
        pushText(out, "**");
        i += 2;
      }
    } else if (c === "*" && depth < MAX_INLINE_DEPTH) {
      let end = s.indexOf("*", i + 1);
      while (end >= 0 && s[end + 1] === "*") end = s.indexOf("*", end + 2);
      if (end > i + 1 && !isSpace(s[i + 1]) && !isSpace(s[end - 1])) {
        out.push({ type: "em", children: parseInline(s.slice(i + 1, end), depth + 1, allowLinks) });
        i = end + 1;
      } else {
        pushText(out, c);
        i += 1;
      }
    } else if (c === "[" && allowLinks && depth < MAX_INLINE_DEPTH) {
      const mid = s.indexOf("](", i + 1);
      const end = mid < 0 ? -1 : closingParen(s, mid + 2);
      if (end < 0) {
        pushText(out, c);
        i += 1;
        continue;
      }
      // a link label holds no link of its own
      const label = parseInline(s.slice(i + 1, mid), depth + 1, false);
      const href = safeHref(s.slice(mid + 2, end));
      if (href) out.push({ type: "link", href, children: label });
      else
        for (const node of label) {
          if (node.type === "text") pushText(out, node.text);
          else out.push(node);
        }
      i = end + 1;
    } else {
      pushText(out, c);
      i += 1;
    }
  }
  return out;
}

const FENCE = /^ {0,3}```[ \t]*([\w+#.-]*)[ \t]*$/;
const HEADING = /^ {0,3}(#{1,6})[ \t]+(\S.*)$/;
const LIST_ITEM = /^ *([-*+]|\d{1,9}[.)])[ \t]+(\S.*)$/;

/** Parses article Markdown into block tokens. Never throws and never emits HTML. */
export function parseMarkdown(markdown: string): Block[] {
  const lines = markdown.replace(/\r\n?/g, "\n").split("\n");
  const blocks: Block[] = [];
  let i = 0;

  const startsBlock = (line: string) => FENCE.test(line) || HEADING.test(line) || LIST_ITEM.test(line);

  while (i < lines.length) {
    const line = lines[i]!;
    if (!line.trim()) {
      i += 1;
      continue;
    }

    const fence = FENCE.exec(line);
    if (fence) {
      const body: string[] = [];
      i += 1;
      while (i < lines.length && !/^ {0,3}```\s*$/.test(lines[i]!)) body.push(lines[i++]!);
      i += 1; // the closing fence (an unterminated one ends with the text)
      blocks.push({ type: "code_block", lang: fence[1] || null, text: body.join("\n") });
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      const level = Math.min(heading[1]!.length, 3) as 1 | 2 | 3;
      blocks.push({ type: "heading", level, children: parseInline(heading[2]!.trim()) });
      i += 1;
      continue;
    }

    const item = LIST_ITEM.exec(line);
    if (item) {
      const ordered = /\d/.test(item[1]!);
      const items: string[] = [item[2]!.trim()];
      i += 1;
      while (i < lines.length) {
        const next = lines[i]!;
        const nextItem = LIST_ITEM.exec(next);
        if (nextItem) {
          if (/\d/.test(nextItem[1]!) !== ordered) break;
          items.push(nextItem[2]!.trim());
        } else if (!next.trim()) {
          // a blank line ends the list unless the next thing is another item of it
          const after = lines.slice(i + 1).find((l) => l.trim());
          const afterItem = after === undefined ? null : LIST_ITEM.exec(after);
          if (!afterItem || /\d/.test(afterItem[1]!) !== ordered) break;
        } else if (/^\s/.test(next)) {
          items[items.length - 1] += ` ${next.trim()}`;
        } else break;
        i += 1;
      }
      blocks.push({ type: "list", ordered, items: items.map((t) => parseInline(t)) });
      continue;
    }

    const text: string[] = [line.trim()];
    i += 1;
    while (i < lines.length && lines[i]!.trim() && !startsBlock(lines[i]!)) text.push(lines[i++]!.trim());
    blocks.push({ type: "paragraph", children: parseInline(text.join(" ")) });
  }
  return blocks;
}
